import { VaultedSecretAccess } from "@p0security/integrations-vaulted-credential-shared";
import { Access } from "@p0security/shared/types/access/index";

/** What both accesses stage: the vaulted secret and the minted user. */
const generated = {
  ...VaultedSecretAccess.generated,
  // `P0_AGENT_<request id>`, derived from the request id at stage, so no
  // requestor can name an existing Snowflake user. It is also the name of the
  // user's wrapper role.
  user: { type: "string", label: "Snowflake user" },
} as const;

const account = {
  type: "dynamic",
  label: "Account",
  description: "Snowflake account identifier, as installed in P0",
} as const;

const warehouse = {
  type: "dynamic",
  label: "Warehouse",
  description:
    "Warehouse granted to the agent as its default. It can also use any warehouse PUBLIC can use and, for role access, any the granted roles can use",
} as const;

// copy of agentKey in packages/integrations/salesforce-ai/shared/src/accesses.ts
/** Names the grant's token secret. Normalize sets it to a random value per
 * request, so delegation, which runs before the request has an id, and stage
 * name the same secret, and no two grants ever share one. */
const key = { type: "generated", label: "Grant key" } as const;

export const SnowflakeAiAccesses = {
  role: Access({
    description:
      "Existing Snowflake roles, granted to an agent's own short-lived Snowflake user",
    label: "Role",
    maturity: "beta",
    delegation: VaultedSecretAccess.delegation,
    generated,
    parameters: {
      account,
      warehouse,
      name: {
        type: "dynamic",
        label: "Roles",
        description: "Existing Snowflake roles to grant",
        multivalued: true,
      },
      key,
    },
    render: {
      locator: "account",
      policy: "name",
    } as const,
  }),
  objects: Access({
    description:
      "Privileges on Snowflake databases, schemas, tables or views, granted to an agent's own short-lived Snowflake user",
    label: "Object privileges",
    maturity: "beta",
    delegation: VaultedSecretAccess.delegation,
    generated,
    parameters: {
      account,
      warehouse,
      // Strings rather than objects, so every request surface (CLI, Slack,
      // Teams, web) can enter them.
      grants: {
        type: "dynamic",
        label: "Grants",
        description:
          "PRIVILEGE:OBJECT entries, where PRIVILEGE is SELECT, INSERT, UPDATE or DELETE, and OBJECT is DB, DB.SCHEMA or DB.SCHEMA.OBJECT",
        multivalued: true,
      },
      key,
    },
    render: {
      locator: "account",
      policy: "grants",
    } as const,
  }),
};

export type SnowflakeAiAccesses = typeof SnowflakeAiAccesses;
export type SnowflakeAiAccess = SnowflakeAiAccesses[keyof SnowflakeAiAccesses];
export type SnowflakeAiRoleAccess = SnowflakeAiAccesses["role"];
export type SnowflakeAiObjectsAccess = SnowflakeAiAccesses["objects"];
